import { useEffect, useState } from 'react';
import { getImageUrl } from '../../utils/imageUtils';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Heart,
  MapPin,
  Building2,
  Home,
  TreeDeciduous,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypeIcons = {
  apartment: Building2,
  villa: Home,
  land: TreeDeciduous,
  plot: TreeDeciduous,
  project: Building2
};

export default function CustomerFavorites() {
  const { getAuthHeader } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customer/favorites`, {
        headers: getAuthHeader()
      });
      setFavorites(response.data.properties);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/customer/favorites/${propertyId}`, {
        headers: getAuthHeader()
      });
      setFavorites(prev => prev.filter(p => p.id !== propertyId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-navy-200 rounded w-1/4 mb-6" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-72 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="customer-favorites">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-6 h-6 text-red-500 fill-red-500" />
        <h1 className="text-2xl font-heading font-bold text-navy-900">My Favorites</h1>
      </div>

      {favorites.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl">
          <Heart className="w-16 h-16 mx-auto mb-4 text-navy-300" />
          <h3 className="text-lg font-semibold text-navy-900 mb-2">No favorites yet</h3>
          <p className="text-navy-600 mb-6">Start exploring and save properties you like</p>
          <Link to="/search">
            <Button className="bg-navy-900 hover:bg-navy-800">
              Browse Properties
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favorites.map((property) => {
            const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
            return (
              <div
                key={property.id}
                className="bg-white rounded-xl overflow-hidden shadow-soft"
                data-testid={`favorite-card-${property.id}`}
              >
                <div className="relative h-48">
                  <Link to={`/agency/${property.agency_slug}/property/${property.id}`}>
                    {property.images?.[0] ? (
                      <img
                        src={getImageUrl(property.images[0])}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-navy-100 flex items-center justify-center">
                        <TypeIcon className="w-16 h-16 text-navy-300" />
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={() => removeFavorite(property.id)}
                    className="absolute top-3 right-3 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    data-testid={`remove-favorite-${property.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <Badge className="absolute top-3 left-3 bg-navy-900/80">
                    {property.property_type.charAt(0).toUpperCase() + property.property_type.slice(1)}
                  </Badge>
                </div>
                <Link to={`/agency/${property.agency_slug}/property/${property.id}`}>
                  <div className="p-4">
                    <h3 className="font-semibold text-navy-900 mb-1 truncate hover:text-sky-500 transition-colors">
                      {property.title}
                    </h3>
                    <p className="text-sm text-navy-500 flex items-center gap-1 mb-3">
                      <MapPin className="w-4 h-4" />
                      {property.location_area}, {property.location_city}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-navy-100">
                      <span className="text-lg font-bold text-navy-900">
                        {formatPrice(property.price)}
                      </span>
                      <span className="text-sm text-navy-500">
                        {property.size} {property.size_unit}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
